/*
 * Tara consultation assessment progress model.
 *
 * Pure logic — no DOM, no globals beyond the export tail. Owns the single
 * decision "what percentage should the generation meter show right now", so
 * tara-consultation-wait.js only paints what this returns.
 *
 * WHY THIS EXISTS
 * The wait meter used to run an ease curve against getAssessmentWaitDuration()
 * — a 3000ms MINIMUM DWELL FLOOR, never an estimate of the real generation.
 * Its elapsed ratio therefore hit 1 after three seconds, at which point
 * `cap = elapsedRatio >= 1 ? 97 : 96` pinned the bar at 97% for the remaining
 * ~20-45s of model work. That is the "moved way too fast, then stuck at 97%"
 * the owner reported.
 *
 * SCOPE — deliberately one phase
 * There is no whole-flow (anchor -> adaptive -> email) bar to model. That
 * markup was removed on 2026-08-13 in 00da3e7 "Port the Saudi consultation
 * runtime to the USA store": `.consultation-progress`,
 * `.consultation-progress__bar` and `.consultation-phase` no longer exist in
 * sections/main-consultation.liquid. The only progress surface a customer
 * sees is the generation meter, `[data-wait-progress]`. Modelling phases that
 * render nowhere buys nothing and costs a second, untested code path — an
 * earlier revision of this file did exactly that and reproduced the reported
 * bug inside the fix for it.
 *
 * THE MODEL
 * One band (floor% -> ceiling%), interpolated on ELAPSED TIME toward the
 * ceiling, asymptotically:
 *
 *     pct = ceiling - (ceiling - floor) * exp(-elapsed / tau)
 *
 * tau comes from the store's rolling average generation time, served by the
 * Worker (see `timing:` records in the CONSULTATION_SESSIONS KV namespace)
 * and folded from real completed consultations. Consequences of the shape:
 *   - never reaches the ceiling, so it can never hit 100 on a timer; only a
 *     real result does that (`complete()`),
 *   - never freezes: past the average it keeps creeping toward, never onto,
 *     the ceiling,
 *   - a slow run decelerates instead of overshooting or sticking,
 *   - monotonic: a ratchet keeps it from ever moving backwards, including
 *     when a freshly served average arrives mid-run or a backgrounded tab
 *     resumes minutes later.
 */
(function () {
  'use strict';

  /*
   * tau is chosen so that at elapsed == average the bar has crossed 80% of
   * its band: 1 - exp(-elapsed/tau) = 0.8 at elapsed = average, therefore
   * tau = average / ln(5). Reads as "about to finish" exactly when the
   * typical run finishes, and leaves real headroom for the slow tail.
   */
  const TAU_DIVISOR = Math.log(5);

  /*
   * SHIPPED DEFAULT — used only until the Worker's rolling average has
   * samples, or when the KV read fails.
   *
   * PROVENANCE (measured, not invented):
   *   assessmentMs 24000
   *     11 complete end-to-end consultations captured against the production
   *     Worker, archived under tara-consultation-project as
   *     "build/sim-<market>-<concern>-<timestamp>/03-assessment-response.json"
   *     (durationMs). n=11, min 19377, p50 21897, mean 25105, p90 32716,
   *     max 45614. Shipped 24000 ~ the mean, trimmed toward the median
   *     because the 45.6s max is a single cold-start tail.
   */
  const DEFAULT_ASSESSMENT_MS = 24000;

  /*
   * The meter is a standalone 0-100 bar on its own "preparing your
   * consultation" screen. It stops at 99 — the last point is bought with a
   * real result, never with a clock.
   */
  const ASSESSMENT_BAND = Object.freeze({ floor: 0, ceiling: 99 });

  function isPositiveFinite(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
  }

  function pickPositive(value, fallback) {
    const numeric = typeof value === 'string' ? Number(value) : value;
    return isPositiveFinite(numeric) ? numeric : fallback;
  }

  /*
   * Accepts whatever the Worker served (or nothing, or something corrupt)
   * and always returns a usable average.
   */
  function normalizeTimings(raw) {
    const source = raw && typeof raw === 'object' ? raw : {};
    return { assessmentMs: pickPositive(source.assessmentMs, DEFAULT_ASSESSMENT_MS) };
  }

  /*
   * Smallest gap we keep between the value and its ceiling. exp(-t/tau)
   * underflows to 0 for a long enough run, which would land the value
   * exactly ON the ceiling and reintroduce the sticking this model exists to
   * prevent. Well below one rendered decimal place, so it is invisible on
   * screen but keeps "approaches, never arrives" true in arithmetic rather
   * than just in intent.
   */
  const CEILING_EPSILON = 1e-6;

  /*
   * The one interpolation. Returns a value inside [floor, ceiling) that
   * approaches but never reaches the ceiling.
   */
  function bandValue(options) {
    const settings = options || {};
    const floor = Number(settings.floor) || 0;
    const ceiling = Number(settings.ceiling) || 0;
    const span = ceiling - floor;
    if (span <= 0) return floor;

    const averageMs = pickPositive(settings.averageMs, DEFAULT_ASSESSMENT_MS);
    const elapsedMs = Math.max(0, Number(settings.elapsedMs) || 0);
    const tau = averageMs / TAU_DIVISOR;

    const value = ceiling - span * Math.exp(-elapsedMs / tau);
    return Math.min(value, ceiling - CEILING_EPSILON);
  }

  /*
   * Stateful wrapper: remembers when generation started, ratchets the value
   * so it is monotonic for the life of the run, and keeps working when a
   * freshly served average is swapped in mid-run.
   */
  function createAssessmentProgressModel(options) {
    const settings = options || {};
    let timings = normalizeTimings(settings.timings);
    const startedAt = Number(settings.now) || 0;
    let ratchet = 0;
    let completed = false;

    function setTimings(next) {
      timings = normalizeTimings(next);
    }

    /*
     * The ratchet is what makes a served-average change or a tab that slept
     * for four minutes safe: the value can rise, never fall.
     */
    function valueAt(now) {
      if (completed) return 100;
      const next = bandValue({
        floor: ASSESSMENT_BAND.floor,
        ceiling: ASSESSMENT_BAND.ceiling,
        elapsedMs: (Number(now) || 0) - startedAt,
        averageMs: timings.assessmentMs,
      });
      if (next > ratchet) ratchet = next;
      return ratchet;
    }

    function complete() {
      completed = true;
      ratchet = 100;
      return 100;
    }

    return {
      setTimings,
      valueAt,
      complete,
      getTimings: function getTimings() { return timings; },
    };
  }

  const API = Object.freeze({
    TAU_DIVISOR,
    CEILING_EPSILON,
    DEFAULT_ASSESSMENT_MS,
    ASSESSMENT_BAND,
    normalizeTimings,
    bandValue,
    createAssessmentProgressModel,
  });

  if (typeof window !== 'undefined') window.TaraConsultationProgressModel = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})();
